class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

if (!customElements.get('search-form')) {
  customElements.define('search-form', SearchForm);
}

(() => {
  const drawerSelector = '#LowsideSearch';
  const cardSelector = '.ls-search-card';
  const detailCache = new Map();
  const syncingCards = new WeakSet();
  const syncedCards = new WeakSet();
  const syncVersion = '5';

  const detailEndpoint = (href) => {
    if (!href) return '';

    try {
      const cardUrl = new URL(href, window.location.origin);
      const cleanPath = cardUrl.pathname.replace(/\/$/, '');
      if (!/\/products\/[^/]+$/.test(cleanPath)) return '';

      const endpoint = new URL(cleanPath, window.location.origin);
      endpoint.searchParams.set('view', 'lowside-card-data');
      endpoint.searchParams.set('ls_min_variant', syncVersion);

      const pageUrl = new URL(window.location.href);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        const value = pageUrl.searchParams.get(key);
        if (value) endpoint.searchParams.set(key, value);
      });

      return `${endpoint.pathname}${endpoint.search}`;
    } catch (error) {
      return '';
    }
  };

  const getDetails = async (endpoint) => {
    if (detailCache.has(endpoint)) return detailCache.get(endpoint);

    const request = fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html,application/xhtml+xml' },
      cache: 'no-store',
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Variant detail request failed with ${response.status}`);
      const text = (await response.text()).trim();
      if (!text) throw new Error('Empty variant detail response');
      return JSON.parse(text);
    });

    detailCache.set(endpoint, request);

    try {
      return await request;
    } catch (error) {
      detailCache.delete(endpoint);
      throw error;
    }
  };

  const ensureImage = (card, imageUrl) => {
    if (!imageUrl) return Promise.resolve(true);
    const media = card.querySelector('span');
    if (!media) return Promise.resolve(false);

    const targetUrl = String(imageUrl);

    return new Promise((resolve) => {
      const preloader = new Image();
      preloader.decoding = 'async';

      preloader.addEventListener(
        'load',
        () => {
          if (!card.isConnected) {
            resolve(false);
            return;
          }

          let image = media.querySelector('img');
          if (!image) {
            image = document.createElement('img');
            image.alt = card.querySelector('strong')?.textContent?.trim() || '';
            media.replaceChildren(image);
          }

          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.loading = 'eager';
          image.decoding = 'async';
          image.src = targetUrl;
          resolve(true);
        },
        { once: true }
      );

      preloader.addEventListener('error', () => resolve(false), { once: true });
      preloader.src = targetUrl;
    });
  };

  const syncCard = async (card) => {
    if (!card || syncingCards.has(card) || syncedCards.has(card)) return;

    const endpoint = detailEndpoint(card.getAttribute('href'));
    if (!endpoint) return;

    syncingCards.add(card);
    card.removeAttribute('data-lowside-min-variant-sync');

    try {
      const details = await getDetails(endpoint);
      if (!details || !details.variant_id) throw new Error('No minimum-price variant found');

      const cardUrl = new URL(card.getAttribute('href'), window.location.origin);
      cardUrl.searchParams.set('variant', String(details.variant_id));
      card.setAttribute('href', `${cardUrl.pathname}${cardUrl.search}${cardUrl.hash}`);

      const price = card.querySelector('b');
      if (price && details.price_formatted) {
        price.textContent = String(details.price_formatted);
      }

      const imageSynced = await ensureImage(card, details.image);
      if (!details.image || imageSynced) syncedCards.add(card);
    } catch (error) {
      syncedCards.delete(card);
    } finally {
      syncingCards.delete(card);
    }
  };

  const syncCards = (drawer) => {
    drawer.querySelectorAll(cardSelector).forEach((card) => syncCard(card));
  };

  const start = () => {
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    document.getElementById('lowside-min-variant-card-style')?.remove();
    syncCards(drawer);

    const observer = new MutationObserver((mutations) => {
      const shouldSync = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(cardSelector) || node.querySelector?.(cardSelector))
        )
      );

      if (shouldSync) syncCards(drawer);
    });

    observer.observe(drawer, { childList: true, subtree: true });

    document.querySelectorAll('[data-ls-search-open]').forEach((button) => {
      button.addEventListener('click', () => syncCards(drawer));
    });

    window.addEventListener('pageshow', () => syncCards(drawer));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
